import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Plus, Trophy, Calendar as CalendarIcon, MapPin, Users,
  QrCode, Trash2, ExternalLink, Copy, Eye, EyeOff, Pencil, RefreshCw,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY_FORM = {
  name: "",
  category: "camp",
  description: "",
  start_date: "",
  end_date: "",
  location: "SCC Padang",
  price: 0,
  currency: "SGD",
  age_groups: "",  // comma-separated string in the form; split on save
  capacity: "",
  registration_open_date: "",
  registration_close_date: "",
  published: false,
  contact_email: "academy@sccfootball.com",
};

function CategoryBadge({ category }) {
  if (category === "tournament") {
    return (
      <Badge className="bg-purple-500/15 text-purple-300 border border-purple-500/40">
        <Trophy className="w-3 h-3 mr-1" /> Tournament
      </Badge>
    );
  }
  return (
    <Badge className="bg-orange-500/15 text-orange-300 border border-orange-500/40">
      <CalendarIcon className="w-3 h-3 mr-1" /> Camp
    </Badge>
  );
}

function PublishedPill({ published }) {
  return published ? (
    <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/40">
      <Eye className="w-3 h-3 mr-1" /> Published
    </Badge>
  ) : (
    <Badge className="bg-slate-700/40 text-slate-300 border border-slate-700">
      <EyeOff className="w-3 h-3 mr-1" /> Draft
    </Badge>
  );
}

function RegistrationStatePill({ status, reason }) {
  // Renders only when the event is in a non-open state so the badge calls
  // attention to "this isn't accepting signups right now". Tooltip carries
  // the human-readable reason from the backend.
  if (!status || status === "open") return null;
  const label = {
    closed: "Registration closed",
    full: "Sold out",
    not_open_yet: "Opens later",
    ended: "Event ended",
  }[status] || status;
  const klass = {
    closed: "bg-rose-500/15 text-rose-300 border border-rose-500/40",
    full: "bg-amber-500/15 text-amber-300 border border-amber-500/40",
    not_open_yet: "bg-sky-500/15 text-sky-300 border border-sky-500/40",
    ended: "bg-slate-600/30 text-slate-400 border border-slate-700",
  }[status] || "bg-slate-700/40 text-slate-300 border border-slate-700";
  return (
    <Badge className={klass} title={reason || ""} data-testid={`event-reg-state-${status}`}>
      {label}
    </Badge>
  );
}

export default function AdminEvents() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);  // event being edited (null = new)
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [qrFor, setQrFor] = useState(null);  // event to show QR for
  const [qrLabelled, setQrLabelled] = useState(true);  // toggle labelled vs bare in modal
  // Per-event money rollup cache: event_id → {paid:{count,amount}, outstanding:..., disputed:..., refunded:...}
  const [rollups, setRollups] = useState({});

  const fetchRollupFor = async (eventId) => {
    try {
      const r = await axios.get(`${API}/admin/events/${eventId}/money-rollup`, { withCredentials: true });
      setRollups(prev => ({ ...prev, [eventId]: r.data }));
    } catch (e) {
      // Soft-fail — don't crash the page if rollup endpoint hiccups
      // eslint-disable-next-line no-console
      console.warn("money-rollup failed", eventId, e);
    }
  };

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/events`, { withCredentials: true });
      setEvents(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load events");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/admin/events`, { withCredentials: true });
        if (!cancelled) {
          const list = r.data || [];
          setEvents(list);
          // Eagerly fetch the money rollup for each event so the card
          // can render "collected / outstanding" without an extra click.
          list.forEach((e) => fetchRollupFor(e.id));
        }
      } catch (e) {
        if (!cancelled) toast.error(e.response?.data?.detail || "Failed to load events");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormOpen(true);
  };

  const openEdit = (e) => {
    setEditing(e);
    setForm({
      name: e.name || "",
      category: e.category || "camp",
      description: e.description || "",
      start_date: e.start_date || "",
      end_date: e.end_date || "",
      location: e.location || "",
      price: e.price ?? 0,
      currency: e.currency || "SGD",
      age_groups: (e.age_groups || []).join(", "),
      capacity: e.capacity ?? "",
      registration_open_date: e.registration_open_date || "",
      registration_close_date: e.registration_close_date || "",
      published: !!e.published,
      contact_email: e.contact_email || "academy@sccfootball.com",
    });
    setFormOpen(true);
  };

  const saveForm = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    if (!form.start_date || !form.end_date) { toast.error("Start and end dates are required"); return; }
    if (form.end_date < form.start_date) { toast.error("End date must be on or after start date"); return; }

    setSaving(true);
    const payload = {
      name: form.name.trim(),
      category: form.category,
      description: form.description || null,
      start_date: form.start_date,
      end_date: form.end_date,
      location: form.location || null,
      price: parseFloat(form.price) || 0,
      currency: form.currency || "SGD",
      age_groups: (form.age_groups || "").split(",").map((s) => s.trim()).filter(Boolean),
      capacity: form.capacity === "" ? null : parseInt(form.capacity, 10),
      registration_open_date: form.registration_open_date || null,
      registration_close_date: form.registration_close_date || null,
      published: !!form.published,
      contact_email: form.contact_email || null,
    };

    try {
      if (editing) {
        await axios.put(`${API}/admin/events/${editing.id}`, payload, { withCredentials: true });
        toast.success(`Updated ${payload.category}`);
      } else {
        await axios.post(`${API}/admin/events`, payload, { withCredentials: true });
        toast.success(`Created ${payload.category}`);
      }
      setFormOpen(false);
      await fetchEvents();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const deleteEvent = async (e) => {
    if (!window.confirm(`Delete "${e.name}"?\n\nThis hides it from the public Events page immediately. Existing registrations are preserved.`)) return;
    try {
      await axios.delete(`${API}/admin/events/${e.id}`, { withCredentials: true });
      toast.success("Deleted");
      await fetchEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <Link to="/admin" className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-4" data-testid="events-back">
          <ArrowLeft className="w-4 h-4" /> Back to Admin
        </Link>

        <div className="flex flex-wrap items-start justify-between gap-3 mb-1">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-2">
              <Trophy className="w-7 h-7 text-purple-400" />
              Camps & Tournaments
            </h1>
            <p className="text-sm text-slate-400 mt-1">
              Create once, appear on the academy site automatically when published. Each event gets a public URL and a downloadable QR code.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/admin/events/master-qrs">
              <Button
                variant="outline"
                size="sm"
                className="border-slate-700 text-slate-300 hover:bg-slate-800"
                data-testid="events-master-qrs-link"
              >
                <QrCode className="w-3 h-3 mr-1" /> Master QRs
              </Button>
            </Link>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchEvents}
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
              data-testid="events-refresh"
            >
              <RefreshCw className={`w-3 h-3 mr-1 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button
              className="bg-purple-600 hover:bg-purple-500 text-white"
              onClick={openNew}
              data-testid="events-create-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> New camp or tournament
            </Button>
          </div>
        </div>

        <div className="mt-6 space-y-3">
          {loading ? (
            <div className="text-slate-400 text-sm py-10 text-center">Loading…</div>
          ) : events.length === 0 ? (
            <Card className="bg-slate-900/60 border-slate-800 p-10 text-center text-slate-400" data-testid="events-empty">
              No camps or tournaments yet. Click <span className="text-purple-300">New camp or tournament</span> to add one.
            </Card>
          ) : (
            events.map((e) => (
              <Card key={e.id} className="bg-slate-900/60 border-slate-800 p-4" data-testid={`event-row-${e.id}`}>
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-base font-semibold text-white truncate">{e.name}</h3>
                      <CategoryBadge category={e.category} />
                      <PublishedPill published={e.published} />
                      <RegistrationStatePill status={e.registration_status} reason={e.registration_reason} />
                      <Badge variant="outline" className="text-xs border-slate-700 text-slate-300 font-mono">
                        /events/{e.slug}
                      </Badge>
                    </div>
                    <div className="text-sm text-slate-400 mt-1 flex flex-wrap gap-x-4 gap-y-1">
                      <span className="inline-flex items-center gap-1"><CalendarIcon className="w-3 h-3" />{e.start_date} → {e.end_date}</span>
                      {e.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{e.location}</span>}
                      <span className="inline-flex items-center gap-1">${e.price?.toFixed?.(2) ?? e.price} {e.currency}</span>
                      {e.capacity != null && (
                        <span className="inline-flex items-center gap-1">
                          <Users className="w-3 h-3" />{e.registration_count || 0} / {e.capacity}
                        </span>
                      )}
                      {(e.age_groups || []).length > 0 && (
                        <span className="text-xs">Ages: {(e.age_groups || []).join(", ")}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-slate-700 text-slate-300 hover:bg-slate-800"
                      onClick={() => copyToClipboard(e.registration_url)}
                      data-testid={`event-copy-url-${e.id}`}
                    >
                      <Copy className="w-3 h-3 mr-1" /> Copy URL
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-slate-700 text-slate-300 hover:bg-slate-800"
                      onClick={() => setQrFor(e)}
                      data-testid={`event-qr-${e.id}`}
                    >
                      <QrCode className="w-3 h-3 mr-1" /> QR
                    </Button>
                    {e.published && (
                      <a href={e.registration_url} target="_blank" rel="noreferrer" data-testid={`event-open-${e.id}`}>
                        <Button size="sm" variant="outline" className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10">
                          <ExternalLink className="w-3 h-3 mr-1" /> Open
                        </Button>
                      </a>
                    )}
                    <Button
                      size="sm"
                      className="bg-slate-700 hover:bg-slate-600 text-white"
                      onClick={() => openEdit(e)}
                      data-testid={`event-edit-${e.id}`}
                    >
                      <Pencil className="w-3 h-3 mr-1" /> Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-rose-500/50 text-rose-300 hover:bg-rose-500/10"
                      onClick={() => deleteEvent(e)}
                      data-testid={`event-delete-${e.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
                {e.description && (
                  <p className="text-xs text-slate-500 mt-3 line-clamp-2">{e.description}</p>
                )}
                {/* Money rollup strip — collected / outstanding / disputed / cancelled / refunded
                    Note: "Cancelled" = registration killed before payment (count only, $0).
                    "Refunded" = money was received and then returned (Jun 2026 fix). */}
                {rollups[e.id] ? (
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-3 pt-3 border-t border-slate-800 text-xs" data-testid={`event-rollup-${e.id}`}>
                    <div className="bg-emerald-500/5 border border-emerald-500/30 rounded px-2 py-1.5">
                      <div className="text-[10px] uppercase tracking-wide text-emerald-300/80">Collected</div>
                      <div className="font-semibold text-emerald-300">${(rollups[e.id].paid?.amount ?? 0).toFixed(2)}</div>
                      <div className="text-[10px] text-slate-500">{rollups[e.id].paid?.count ?? 0} paid</div>
                    </div>
                    <div className="bg-amber-500/5 border border-amber-500/30 rounded px-2 py-1.5">
                      <div className="text-[10px] uppercase tracking-wide text-amber-300/80">Outstanding</div>
                      <div className="font-semibold text-amber-300">${(rollups[e.id].outstanding?.amount ?? 0).toFixed(2)}</div>
                      <div className="text-[10px] text-slate-500">{rollups[e.id].outstanding?.count ?? 0} awaiting</div>
                    </div>
                    <div className="bg-rose-500/5 border border-rose-500/30 rounded px-2 py-1.5">
                      <div className="text-[10px] uppercase tracking-wide text-rose-300/80">Disputed</div>
                      <div className="font-semibold text-rose-300">${(rollups[e.id].disputed?.amount ?? 0).toFixed(2)}</div>
                      <div className="text-[10px] text-slate-500">{rollups[e.id].disputed?.count ?? 0} disputes</div>
                    </div>
                    <div
                      className="bg-slate-800/30 border border-slate-700/60 rounded px-2 py-1.5"
                      title="Registrations cancelled before any payment was received. Zero monetary impact."
                    >
                      <div className="text-[10px] uppercase tracking-wide text-slate-400">Cancelled</div>
                      <div className="font-semibold text-slate-300">{rollups[e.id].cancelled?.count ?? 0}</div>
                      <div className="text-[10px] text-slate-500">no money received</div>
                    </div>
                    <div
                      className="bg-slate-700/20 border border-slate-700/50 rounded px-2 py-1.5"
                      title="Payments that were received and then returned to the parent."
                    >
                      <div className="text-[10px] uppercase tracking-wide text-slate-400">Refunded</div>
                      <div className="font-semibold text-slate-300">${(rollups[e.id].refunded?.amount ?? 0).toFixed(2)}</div>
                      <div className="text-[10px] text-slate-500">{rollups[e.id].refunded?.count ?? 0} refunds</div>
                    </div>
                  </div>
                ) : null}
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={formOpen} onOpenChange={(o) => { if (!saving) setFormOpen(o); }}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit" : "New"} {form.category}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-xs text-slate-400 block mb-1">Name *</label>
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Spring Holiday Football Camp 2026"
                className="bg-slate-800 border-slate-700"
                data-testid="event-form-name"
              />
            </div>

            <div className="flex gap-2">
              {["camp", "tournament"].map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, category: c }))}
                  className={`flex-1 px-3 py-2 rounded text-sm font-medium border transition ${
                    form.category === c
                      ? c === "tournament"
                        ? "bg-purple-500/20 border-purple-500/60 text-purple-200"
                        : "bg-orange-500/20 border-orange-500/60 text-orange-200"
                      : "bg-slate-800 border-slate-700 text-slate-400 hover:text-slate-200"
                  }`}
                  data-testid={`event-form-category-${c}`}
                >
                  {c === "tournament" ? "🏆 Tournament" : "🏕 Camp"}
                </button>
              ))}
            </div>

            <div>
              <label className="text-xs text-slate-400 block mb-1">Description</label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Short description for the academy website"
                className="bg-slate-800 border-slate-700"
                rows={3}
                data-testid="event-form-description"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-400 block mb-1">Start date *</label>
                <Input
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))}
                  className="bg-slate-800 border-slate-700"
                  data-testid="event-form-start"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1">End date *</label>
                <Input
                  type="date"
                  value={form.end_date}
                  onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))}
                  className="bg-slate-800 border-slate-700"
                  data-testid="event-form-end"
                />
              </div>
            </div>

            <div>
              <label className="text-xs text-slate-400 block mb-1">Location</label>
              <Input
                value={form.location}
                onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                className="bg-slate-800 border-slate-700"
                data-testid="event-form-location"
              />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-slate-400 block mb-1">Price</label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.price}
                  onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
                  className="bg-slate-800 border-slate-700"
                  data-testid="event-form-price"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1">Currency</label>
                <Input
                  value={form.currency}
                  onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))}
                  className="bg-slate-800 border-slate-700"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1">Capacity</label>
                <Input
                  type="number"
                  value={form.capacity}
                  onChange={(e) => setForm((f) => ({ ...f, capacity: e.target.value }))}
                  className="bg-slate-800 border-slate-700"
                  placeholder="leave blank = unlimited"
                  data-testid="event-form-capacity"
                />
              </div>
            </div>

            <div>
              <label className="text-xs text-slate-400 block mb-1">Age groups (comma-separated)</label>
              <Input
                value={form.age_groups}
                onChange={(e) => setForm((f) => ({ ...f, age_groups: e.target.value }))}
                placeholder="e.g. U6, U8, U10, U12"
                className="bg-slate-800 border-slate-700"
                data-testid="event-form-age-groups"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-400 block mb-1">Registration opens</label>
                <Input
                  type="date"
                  value={form.registration_open_date}
                  onChange={(e) => setForm((f) => ({ ...f, registration_open_date: e.target.value }))}
                  className="bg-slate-800 border-slate-700"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1">Registration closes</label>
                <Input
                  type="date"
                  value={form.registration_close_date}
                  onChange={(e) => setForm((f) => ({ ...f, registration_close_date: e.target.value }))}
                  className="bg-slate-800 border-slate-700"
                />
              </div>
            </div>

            <div>
              <label className="text-xs text-slate-400 block mb-1">Contact email</label>
              <Input
                value={form.contact_email}
                onChange={(e) => setForm((f) => ({ ...f, contact_email: e.target.value }))}
                className="bg-slate-800 border-slate-700"
              />
            </div>

            <div className="flex items-center gap-3 bg-slate-800/50 px-3 py-2 rounded">
              <Switch
                checked={form.published}
                onCheckedChange={(v) => setForm((f) => ({ ...f, published: v }))}
                data-testid="event-form-published"
              />
              <div>
                <div className="text-sm text-white font-medium">{form.published ? "Published" : "Draft"}</div>
                <div className="text-xs text-slate-400">
                  {form.published
                    ? "Visible on the academy website and on /api/public/events"
                    : "Hidden from the public — only admins can see this"}
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              className="border-slate-700"
              onClick={() => setFormOpen(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              className="bg-purple-600 hover:bg-purple-500"
              onClick={saveForm}
              disabled={saving}
              data-testid="event-form-save"
            >
              {saving ? "Saving…" : (editing ? "Save changes" : `Create ${form.category}`)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR code dialog */}
      <Dialog open={!!qrFor} onOpenChange={(o) => { if (!o) setQrFor(null); }}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100">
          <DialogHeader>
            <DialogTitle>QR code — {qrFor?.name}</DialogTitle>
          </DialogHeader>
          {qrFor && (
            <div className="text-center">
              <div className="bg-white inline-block p-3 rounded max-w-full">
                <img
                  src={`${BACKEND_URL}/api/events/${qrFor.slug}/qr.png?labelled=${qrLabelled ? "true" : "false"}`}
                  alt="Registration QR code"
                  className="block max-h-[400px] w-auto h-auto mx-auto"
                  data-testid="event-qr-image"
                />
              </div>
              <p className="text-xs text-slate-400 mt-3 font-mono break-all">
                {qrFor.registration_url}
              </p>
              <div className="flex items-center justify-center gap-2 mt-3" data-testid="event-qr-style-toggle">
                <span className="text-xs uppercase tracking-wide text-slate-500">Style</span>
                <div className="inline-flex rounded-md border border-slate-700 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setQrLabelled(true)}
                    className={`px-3 py-1 text-xs font-medium transition-colors ${qrLabelled ? "bg-slate-100 text-slate-900" : "text-slate-300 hover:bg-slate-800"}`}
                    data-testid="event-qr-style-labelled"
                  >
                    Labelled (with logo)
                  </button>
                  <button
                    type="button"
                    onClick={() => setQrLabelled(false)}
                    className={`px-3 py-1 text-xs font-medium transition-colors border-l border-slate-700 ${!qrLabelled ? "bg-slate-100 text-slate-900" : "text-slate-300 hover:bg-slate-800"}`}
                    data-testid="event-qr-style-bare"
                  >
                    Bare QR
                  </button>
                </div>
              </div>
              <div className="flex gap-2 mt-4 justify-center">
                <Button
                  variant="outline"
                  className="border-slate-700"
                  onClick={() => copyToClipboard(qrFor.registration_url)}
                >
                  <Copy className="w-3 h-3 mr-1" /> Copy URL
                </Button>
                <a
                  href={`${BACKEND_URL}/api/events/${qrFor.slug}/qr.png?labelled=${qrLabelled ? "true" : "false"}`}
                  download={`qr-${qrFor.slug}${qrLabelled ? "" : "-bare"}.png`}
                >
                  <Button className="bg-purple-600 hover:bg-purple-500" data-testid="event-qr-download">
                    Download PNG
                  </Button>
                </a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
